import { ChevronDown, ChevronRight } from "lucide-react";
import clsx from "clsx";
import type { PanelSectionHeaderProps, PanelSectionTone } from "./types";

// The section's mark IS the mark its things carry on the drawing: a removal is
// a red disc, a device on no run is a hollow violet square. Colour says what a
// thing is, never that a section is important.
const MARK: Record<PanelSectionTone, string> = {
  neutral: "h-2.5 w-2.5 rounded-full bg-slate-400",
  removed: "h-2.5 w-2.5 rounded-full bg-rose-500",
  unrouted: "h-2.5 w-2.5 border-2 border-violet-600",
  // A grey BAND, because that is what a tray is on the drawing — a length of
  // infrastructure, not a point.
  tray: "h-1.5 w-4 rounded-full bg-slate-400",
  // Heavier, squared off and stone: the drawing's own wall, and deliberately
  // not the tray's band — one carries cable and the other stops it.
  wall: "h-2 w-4 rounded-sm bg-stone-700",
  room: "h-3 w-4 rounded-[3px] border border-slate-500 bg-slate-200",
};

const CHIP: Record<PanelSectionTone, string> = {
  neutral: "bg-slate-100 text-slate-700",
  removed: "bg-rose-100 text-rose-800",
  unrouted: "bg-violet-100 text-violet-800",
  tray: "bg-slate-100 text-slate-700",
  wall: "bg-stone-200 text-stone-800",
  room: "bg-slate-200 text-slate-800",
};

export const PanelSectionHeader = ({
  tone,
  title,
  count,
  action,
  isOpen,
  onToggle,
}: PanelSectionHeaderProps) => {
  const inside = (
    <>
      <span aria-hidden className={clsx("flex-none", MARK[tone])} />
      <span className="min-w-0 truncate text-[14px] font-semibold text-slate-800">
        {title}
      </span>
      {count !== undefined && (
        <span
          className={clsx(
            "flex-none rounded px-1.5 py-0.5 text-[13px] font-semibold tabular-nums",
            CHIP[tone],
          )}
        >
          {count}
        </span>
      )}
      {isOpen !== undefined && (
        <span className="ml-auto flex-none text-slate-500">
          {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </span>
      )}
    </>
  );

  return (
    <div className="flex flex-none items-center">
      {/* A header that does not collapse is not a button. The runs block has
          nothing to disclose — it IS the list — and a button that does nothing
          on click is the same lie as a glyph nobody can read. */}
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          className="flex h-10 min-w-0 flex-1 items-center gap-2 px-2.5 text-left hover:bg-slate-50"
        >
          {inside}
        </button>
      ) : (
        <div className="flex h-10 min-w-0 flex-1 items-center gap-2 px-2.5">
          {inside}
        </div>
      )}
      {action}
    </div>
  );
};
